import { ChangeDetectorRef, Component, ElementRef, Inject, Input, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { fabric } from 'fabric';
import { distinctUntilChanged } from 'rxjs/operators';
import { StaffService } from 'src/app/core/api/staff.service';
import { EConsultFormGroup } from 'src/app/econsult/econsult-form.model';
import { DataSrcService } from 'src/app/shared/enlargeable-media/data-src.service';
import { InteractionSignOffGroup } from './interaction-signoff.model';

@Component({
  selector: 'csi-interaction-signoff',
  templateUrl: './interaction-signoff.component.html',
  styleUrls: ['./interaction-signoff.component.scss']
})
export class InteractionSignoffComponent {
  @Input() isProfileSignature = false;
  @Input() doctorId: string = '';
  @ViewChild('fabricSurface', { static: true }) canvasEl: ElementRef<HTMLCanvasElement>;
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef<HTMLInputElement>;

  interactionSignOff: InteractionSignOffGroup;
  selectedSignatureTab = 0;
  fileError: 'size' | 'type' | null = null;
  private canvas: fabric.Canvas;
  readonly CANVAS_WIDTH = 550;
  readonly CANVAS_HEIGHT = 150;
  readonly MAX_FILE_SIZE = 300 * 1024;

  public showApplyButton = false;

  public doctorFormControl = new FormControl('');

  public signatureTypeMap: Record<number, 'drawn' | 'typed' | 'upload'> = {
    0: 'drawn',
    1: 'typed',
    2: 'upload'
  };

  constructor(
    public dialogRef: MatDialogRef<InteractionSignoffComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      interactionSignOff?: InteractionSignOffGroup;
      parentFormGroup?: EConsultFormGroup;
      isProfileSignature?: boolean;
      doctorId?: string;
    },
    private staffService: StaffService,
    private changeDetectorRef: ChangeDetectorRef,
    public dataSrcService: DataSrcService
  ) {
    this.interactionSignOff = data.interactionSignOff || new InteractionSignOffGroup();
    this.isProfileSignature = data.isProfileSignature || false;
    this.doctorId = data.doctorId || '';

    if (data.parentFormGroup) {
      const initialDocId =
        data.parentFormGroup.manualEntryControls.doctorInformationForm.controls.id.value;
      if (initialDocId) {
        this.interactionSignOff.controls.doctorSigning.setValue(initialDocId);
      }

      data.parentFormGroup.manualEntryControls.doctorInformationForm.controls.id.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe(newDoctorId => {
          this.interactionSignOff.controls.doctorSigning.setValue(newDoctorId);
        });
    } else if (this.isProfileSignature) {
      this.interactionSignOff.controls.doctorSigning.setValue(this.staffService.staff.id);
      this.interactionSignOff.controls.doctorSigning.disable();
    }

    if (this.interactionSignOffControls.signatureType.value) {
      this.selectedSignatureTab = this.getTabIndexFromSignatureType(
        this.interactionSignOffControls.signatureType.value
      );
    }
    this.showApplyButton = !this.isProfileSignature;
  }

  ngOnInit() {
    if (this.interactionSignOff && !this.interactionSignOff.controls.dateAndTime.value) {
      this.interactionSignOff.controls.dateAndTime.setValue(new Date().toISOString());
    }
  }

  ngAfterViewInit() {
    this.initializeCanvas();
    this.setActiveTabBasedOnSignature();
    this.loadExistingSignature();

    if (this.selectedSignatureTab === 1) {
      this.interactionSignOffControls.typedSignature.updateValueAndValidity();
    } else if (this.selectedSignatureTab === 2) {
      this.interactionSignOffControls.uploadedSignature.updateValueAndValidity();
    }
  }

  applyFromProfile() {
    const doctorSignature = this.staffService.staff.doctorSignature;
    if (doctorSignature) {
      this.canvas.clear().renderAll();

      const signatureType = doctorSignature.drawn
        ? 'drawn'
        : doctorSignature.typed
        ? 'typed'
        : 'upload';

      this.interactionSignOff.patchValue({
        signatureType: signatureType,
        drawnSignature: this.convertSignatureData(doctorSignature.drawn),
        typedSignature: doctorSignature.typed,
        uploadedSignature: doctorSignature.uploaded
      });

      // Force UI update
      this.setActiveTabBasedOnSignature();
      this.loadExistingSignature();
      this.changeDetectorRef.detectChanges();
    }
  }

  private convertSignatureData(data: any): any {
    // Handle stringified canvas data
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.error('Error parsing signature data:', e);
        return null;
      }
    }
    return data;
  }

  private setActiveTabBasedOnSignature() {
    const controls = this.interactionSignOff.controls;

    if (controls.uploadedSignature.value) {
      this.selectedSignatureTab = 2;
    } else if (controls.typedSignature.value) {
      this.selectedSignatureTab = 1;
    } else if (controls.drawnSignature.value) {
      this.selectedSignatureTab = 0;
    } else {
      this.selectedSignatureTab = 0;
    }

    if (this.selectedSignatureTab === 0) {
      this.loadExistingSignature();
    }

    this.changeDetectorRef.detectChanges();
  }

  private getTabIndexFromSignatureType(type: string): number {
    const map = {
      drawn: 0,
      typed: 1,
      upload: 2
    };
    const tabIndex = map[type] || 0;
    return tabIndex;
  }

  private loadExistingSignature() {
    this.canvas.clear();
    const sig = this.interactionSignOffControls.drawnSignature.value;

    if (sig) {
      this.canvas.loadFromJSON(sig, () => {
        this.canvas.getObjects().forEach(obj => {
          obj.set({ selectable: false, evented: false });
        });
        this.canvas.renderAll();
        this.zoomToFitCanvasContent();
      });
    }
  }

  private zoomToFitCanvasContent() {
    const objects = this.canvas.getObjects();
    if (objects.length > 0) {
      const boundingRect = this.canvas.getObjects().reduce(
        (acc, obj) => {
          const rect = obj.getBoundingRect(true);
          return {
            left: Math.min(acc.left, rect.left),
            top: Math.min(acc.top, rect.top),
            width: Math.max(acc.width, rect.left + rect.width),
            height: Math.max(acc.height, rect.top + rect.height)
          };
        },
        { left: Infinity, top: Infinity, width: 0, height: 0 }
      );

      const scale =
        Math.min(this.CANVAS_WIDTH / boundingRect.width, this.CANVAS_HEIGHT / boundingRect.height) *
        0.9;

      this.canvas.zoomToPoint({ x: this.CANVAS_WIDTH / 2, y: this.CANVAS_HEIGHT / 2 }, scale);
      this.canvas.renderAll();
    }
  }

  private initializeCanvas() {
    this.canvas = new fabric.Canvas(this.canvasEl.nativeElement, {
      isDrawingMode: true,
      enableRetinaScaling: false,
      selection: false
    });
    this.canvas.setWidth(this.CANVAS_WIDTH);
    this.canvas.setHeight(this.CANVAS_HEIGHT);

    this.canvas.on('path:created', (event: any) => {
      const path: fabric.Path = event.path;
      path.set({ fill: null, selectable: false, evented: false });
      this.handleSignatureChange(0);
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];

    if (!file) return;

    if (!this.validateFile(file)) return;

    const reader = new FileReader();
    reader.onload = () => {
      this.interactionSignOff.patchValue(
        {
          signatureType: 'upload',
          uploadedSignature: this.isProfileSignature ? file : reader.result,
          drawnSignature: null,
          typedSignature: null
        },
        { emitEvent: false }
      );
    };
    reader.readAsDataURL(file);
  }

  removeFile() {
    this.interactionSignOff.patchValue({
      uploadedSignature: null,
      signatureType: null
    });

    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }

    const ctrl = this.interactionSignOff.controls.uploadedSignature;
    ctrl.reset();
    ctrl.updateValueAndValidity();
  }

  private validateFile(file: File): boolean {
    if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
      this.fileError = 'type';
      return false;
    }

    if (file.size > this.MAX_FILE_SIZE) {
      this.fileError = 'size';
      return false;
    }

    this.fileError = null;
    return true;
  }

  onSubmit() {
    this.interactionSignOff.markAllAsTouched();

    if (this.interactionSignOff.valid) {
      const signatureData = this.interactionSignOff.getRawValue();

      if (signatureData.drawnSignature) {
        signatureData.drawnSignature = JSON.stringify(signatureData.drawnSignature);
      }

      this.dialogRef.close(signatureData);
    }
  }

  handleSignatureChange(index: number) {
    const type = this.signatureTypeMap[index];
    this.interactionSignOffControls.signatureType.setValue(type);

    this.updateSignatureValidation(type);
  }

  private updateSignatureValidation(type: 'drawn' | 'typed' | 'upload') {
    const controls = this.interactionSignOffControls;

    controls.drawnSignature.clearValidators();
    controls.typedSignature.clearValidators();
    controls.uploadedSignature.clearValidators();

    if (type === 'drawn') {
      controls.drawnSignature.setValidators(Validators.required);
      this.saveDrawnSignature();
    } else if (type === 'typed') {
      controls.typedSignature.setValidators(Validators.required);
    } else {
      controls.uploadedSignature.setValidators(Validators.required);
    }

    controls.drawnSignature.updateValueAndValidity();
    controls.typedSignature.updateValueAndValidity();
    controls.uploadedSignature.updateValueAndValidity();
  }

  private saveDrawnSignature() {
    if (this.canvas.getObjects().length > 0) {
      this.interactionSignOffControls.drawnSignature.setValue(this.canvas.toJSON());
    }
  }

  clearCanvas() {
    this.canvas.clear();
    this.interactionSignOff.patchValue({
      drawnSignature: null
    });
  }

  get interactionSignOffControls() {
    return this.interactionSignOff.controls as InteractionSignOffGroup['controls'];
  }
}
